/**
 * Environment variable utilities for client packages
 * Provides framework-agnostic access to environment variables
 * Respects client-side exposure rules for each framework
 *
 * Note: Firebase configs are hardcoded in firebase/config.ts
 * This utility only handles app-level environment variables
 *
 * Most environment detection is delegated to @order-up/common for consistency
 */

import {
  getAppEnv as getCommonAppEnv,
  getOnboardingServiceUrl as getCommonOnboardingServiceUrl,
  getManagementServiceUrl as getCommonManagementServiceUrl,
  getFrameworkAgnosticEnvVar,
  getRedirectUrls,
  getServiceUrls
} from '@order-up/common';
import { createLogger } from './logger';

const logger = createLogger('EnvConfig');

// Declare the global variable that Vite defines via the define config
// Note: VITE_ prefix is stripped in __VITE_ENV__ (e.g., VITE_API_URL → API_URL)
declare const __VITE_ENV__: {
  AUTH_SERVICE_URL?: string;
  API_URL?: string;
  APP_ENV?: string;
  MODE?: string;
  DEV_HOST?: string;
  ENABLE_CROSS_DEVICE?: string;
  USE_EMULATOR?: string;
  AUTOMATION_BYPASS_SECRET?: string;  // From VITE_AUTOMATION_BYPASS_SECRET
  RECAPTCHA_ENTERPRISE_SITE_KEY?: string; // From VITE_RECAPTCHA_ENTERPRISE_SITE_KEY
  FIREBASE_APPCHECK_DEBUG_TOKEN?: string; // From VITE_FIREBASE_APPCHECK_DEBUG_TOKEN
} | undefined;

// Cache for getEnvVars() - environment variables don't change at runtime
let _envVarsCache: ReturnType<typeof getEnvVarsInternal> | null = null;

/**
 * Gets environment variables with proper Vite/Next.js detection.
 * Results are cached after first call since env vars don't change at runtime.
 */
export const getEnvVars = () => {
  if (_envVarsCache) return _envVarsCache;
  _envVarsCache = getEnvVarsInternal();
  return _envVarsCache;
};

const getEnvVarsInternal = () => {
  // Check if we're in a browser environment
  if (typeof window !== 'undefined') {
    // Browser environment - detect framework directly here (not in common package)
    // Vite's define config only replaces __VITE_ENV__ in the app bundle, not pre-bundled packages
    const isVite = typeof __VITE_ENV__ !== 'undefined' ||
      typeof (globalThis as any).__VITE_ENV__ !== 'undefined' ||
      typeof (globalThis as any).__vite__ !== 'undefined';

    logger.debug('Framework detection', {
      isVite,
      hasDirectViteEnv: typeof __VITE_ENV__ !== 'undefined',
      hasGlobalViteEnv: typeof (globalThis as any).__VITE_ENV__ !== 'undefined'
    });

    if (isVite) {
      // Vite environment - try Vite-specific access methods
      try {
        // 1. Try the __VITE_ENV__ global defined in vite.config.ts (most reliable)
        if (typeof (globalThis as any).__VITE_ENV__ !== 'undefined') {
          const viteEnv = (globalThis as any).__VITE_ENV__;
          logger.debug('Using Vite defined environment variables', viteEnv);
          return {
            AUTH_SERVICE_URL: viteEnv.AUTH_SERVICE_URL,
            API_URL: viteEnv.API_URL,
            APP_ENV: viteEnv.APP_ENV || 'development',
            MODE: viteEnv.MODE || 'development',
            USE_EMULATOR: viteEnv.USE_EMULATOR,
            ENABLE_CROSS_DEVICE: viteEnv.ENABLE_CROSS_DEVICE,
            DEV_HOST: viteEnv.DEV_HOST,
            RECAPTCHA_ENTERPRISE_SITE_KEY: viteEnv.RECAPTCHA_ENTERPRISE_SITE_KEY,
            FIREBASE_APPCHECK_DEBUG_TOKEN: viteEnv.FIREBASE_APPCHECK_DEBUG_TOKEN,
          };
        }

        // 2. Try direct __VITE_ENV__ access (for build-time replacement)
        if (typeof __VITE_ENV__ !== 'undefined') {
          logger.debug('Using direct __VITE_ENV__ access', __VITE_ENV__);
          return {
            AUTH_SERVICE_URL: __VITE_ENV__.AUTH_SERVICE_URL,
            API_URL: __VITE_ENV__.API_URL,
            APP_ENV: __VITE_ENV__.APP_ENV || 'development',
            MODE: __VITE_ENV__.MODE || 'development',
            USE_EMULATOR: __VITE_ENV__.USE_EMULATOR,
            ENABLE_CROSS_DEVICE: __VITE_ENV__.ENABLE_CROSS_DEVICE,
            DEV_HOST: __VITE_ENV__.DEV_HOST,
            RECAPTCHA_ENTERPRISE_SITE_KEY: __VITE_ENV__.RECAPTCHA_ENTERPRISE_SITE_KEY,
            FIREBASE_APPCHECK_DEBUG_TOKEN: __VITE_ENV__.FIREBASE_APPCHECK_DEBUG_TOKEN,
          };
        }

        // 3. Try import.meta.env if available (Vite fallback) - ONLY in Vite
        const fn = new Function('return typeof import !== "undefined" && import.meta && import.meta.env');
        const importMetaEnv = fn();
        if (importMetaEnv && importMetaEnv.VITE_AUTH_SERVICE_URL) {
          logger.debug('Using import.meta.env (Vite)');
          return {
            AUTH_SERVICE_URL: importMetaEnv.VITE_AUTH_SERVICE_URL,
            API_URL: importMetaEnv.VITE_API_URL,
            APP_ENV: importMetaEnv.VITE_APP_ENV || 'development',
            MODE: importMetaEnv.MODE || 'development',
            USE_EMULATOR: importMetaEnv.VITE_USE_EMULATOR,
            ENABLE_CROSS_DEVICE: importMetaEnv.VITE_ENABLE_CROSS_DEVICE,
            DEV_HOST: importMetaEnv.VITE_DEV_HOST,
            RECAPTCHA_ENTERPRISE_SITE_KEY: importMetaEnv.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY,
            FIREBASE_APPCHECK_DEBUG_TOKEN: importMetaEnv.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN,
          };
        }
      } catch (error) {
        logger.warn('Failed to access Vite environment variables', { error: String(error) });
      }

      // Vite fallback
      logger.warn('Vite detected but no environment variables found, using defaults');
      return {
        AUTH_SERVICE_URL: undefined,
        API_URL: undefined,
        APP_ENV: 'development',
        MODE: 'development',
        USE_EMULATOR: undefined,
        ENABLE_CROSS_DEVICE: undefined,
        DEV_HOST: undefined,
        RECAPTCHA_ENTERPRISE_SITE_KEY: undefined,
        FIREBASE_APPCHECK_DEBUG_TOKEN: undefined,
      };
    } else {
      // Next.js environment - use direct references for build-time inlining
      // Direct references to process.env.NEXT_PUBLIC_* are inlined by Next.js at build time
      // Dynamic checks like `typeof process !== 'undefined'` prevent inlining
      const AUTH_SERVICE_URL = process.env.NEXT_PUBLIC_AUTH_SERVICE_URL;
      const API_URL = process.env.NEXT_PUBLIC_API_URL;
      const APP_ENV = process.env.NEXT_PUBLIC_APP_ENV;
      const NODE_ENV = process.env.NODE_ENV;
      const USE_EMULATOR = process.env.NEXT_PUBLIC_USE_EMULATOR;
      const ENABLE_CROSS_DEVICE = process.env.NEXT_PUBLIC_ENABLE_CROSS_DEVICE;
      const DEV_HOST = process.env.NEXT_PUBLIC_DEV_HOST;
      const RECAPTCHA_ENTERPRISE_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY;
      const FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.NEXT_PUBLIC_FIREBASE_APPCHECK_DEBUG_TOKEN;

      logger.debug('Using Next.js environment variables (direct references)');
      return {
        AUTH_SERVICE_URL,
        API_URL,
        APP_ENV: APP_ENV || 'development',
        MODE: NODE_ENV || 'development',
        USE_EMULATOR,
        ENABLE_CROSS_DEVICE,
        DEV_HOST,
        RECAPTCHA_ENTERPRISE_SITE_KEY,
        FIREBASE_APPCHECK_DEBUG_TOKEN,
      };
    }
  } else {
    // Server-side environment (Node.js)
    return {
      AUTH_SERVICE_URL: process.env.NEXT_PUBLIC_AUTH_SERVICE_URL || process.env.AUTH_SERVICE_URL,
      API_URL: process.env.NEXT_PUBLIC_API_URL || process.env.API_URL,
      APP_ENV: process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV || 'development',
      MODE: process.env.NODE_ENV || 'development',
      USE_EMULATOR: process.env.NEXT_PUBLIC_USE_EMULATOR || process.env.USE_EMULATOR,
      ENABLE_CROSS_DEVICE: process.env.NEXT_PUBLIC_ENABLE_CROSS_DEVICE || process.env.ENABLE_CROSS_DEVICE,
      DEV_HOST: process.env.NEXT_PUBLIC_DEV_HOST || process.env.DEV_HOST,
      RECAPTCHA_ENTERPRISE_SITE_KEY: process.env.NEXT_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY,
      FIREBASE_APPCHECK_DEBUG_TOKEN: process.env.NEXT_PUBLIC_FIREBASE_APPCHECK_DEBUG_TOKEN,
    };
  }
};

/**
 * Gets the authentication service URL with dynamic host detection
 * @returns The auth service URL or undefined if not set
 */
export const getAuthServiceUrl = (): string | undefined => {
  const envVars = getEnvVars();
  if (envVars.AUTH_SERVICE_URL) {
    return envVars.AUTH_SERVICE_URL;
  }

  // Fallback to dynamic service URLs
  try {
    const serviceUrls = getServiceUrls();
    return serviceUrls.auth;
  } catch (error) {
    logger.warn('Failed to resolve AUTH_SERVICE_URL', { error: String(error) });
    return undefined;
  }
};

/**
 * Gets the API service URL with dynamic host detection
 * @returns The API service URL or undefined if not set
 */
export const getApiUrl = (): string | undefined => {
  const envVars = getEnvVars();
  if (envVars.API_URL) {
    return envVars.API_URL;
  }

  // Fallback to dynamic service URLs
  try {
    const serviceUrls = getServiceUrls();
    return serviceUrls.api;
  } catch (error) {
    logger.warn('Failed to resolve API URL', { error: String(error) });
    return undefined;
  }
};

/**
 * Gets the current application environment
 * Uses shared implementation from @order-up/common
 * @returns The app environment (development, staging, production)
 */
export const getAppEnv = (): string => {
  return getCommonAppEnv();
};

/**
 * Gets NODE_ENV in a framework-agnostic way
 * - Vite: Uses import.meta.env.MODE (development/production based on command)
 * - Next.js/Node.js: Uses process.env.NODE_ENV
 */
export const getNodeEnv = (): string => {
  const envVars = getEnvVars();
  return envVars.MODE;
};

/**
 * Checks if the app is running in development mode
 */
export const isDevelopment = (): boolean => {
  return getNodeEnv() === 'development';
};

/**
 * Checks if the app is running in production mode
 */
export const isProduction = (): boolean => {
  return getNodeEnv() === 'production';
};

/**
 * Gets the automation bypass secret for testing/automation
 * @returns The automation bypass secret or undefined if not set
 */
export const getAutomationBypassSecret = (): string | undefined => {
  if (typeof window !== 'undefined') {
    // Check for Vite directly (Vite's define replaces __VITE_ENV__ in app bundle)
    const isVite = typeof __VITE_ENV__ !== 'undefined' ||
      typeof (globalThis as any).__VITE_ENV__ !== 'undefined';

    if (isVite) {
      // Vite environment - check direct __VITE_ENV__ first
      try {
        if (typeof __VITE_ENV__ !== 'undefined' && __VITE_ENV__.AUTOMATION_BYPASS_SECRET) {
          logger.debug('Using Vite AUTOMATION_BYPASS_SECRET');
          return __VITE_ENV__.AUTOMATION_BYPASS_SECRET;
        }
        if (typeof (globalThis as any).__VITE_ENV__ !== 'undefined') {
          logger.debug('Using globalThis Vite AUTOMATION_BYPASS_SECRET');
          return (globalThis as any).__VITE_ENV__.AUTOMATION_BYPASS_SECRET;
        }
      } catch (error) {
        logger.warn('Failed to access Vite AUTOMATION_BYPASS_SECRET', { error: String(error) });
      }
      return undefined;
    } else {
      // Next.js environment
      try {
        if (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_AUTOMATION_BYPASS_SECRET) {
          logger.debug('Using Next.js AUTOMATION_BYPASS_SECRET');
          return process.env.NEXT_PUBLIC_AUTOMATION_BYPASS_SECRET;
        }
      } catch (error) {
        logger.warn('Failed to access Next.js AUTOMATION_BYPASS_SECRET', { error: String(error) });
      }
      return undefined;
    }
  } else {
    // Server-side
    return process.env.NEXT_PUBLIC_AUTOMATION_BYPASS_SECRET || process.env.AUTOMATION_BYPASS_SECRET;
  }
};

/**
 * Gets redirect URLs for authentication flows with dynamic host detection
 * Uses shared implementation from @order-up/common with fallback
 * @returns Object with redirect URLs for different apps
 */
export const getAuthRedirectUrls = () => {
  try {
    return getRedirectUrls();
  } catch {
    // Fallback to environment variables using shared helper
    const envVars = getEnvVars();

    return {
      default: envVars.AUTH_SERVICE_URL || 'http://localhost:3000',
      admin: getFrameworkAgnosticEnvVar('ADMIN_REDIRECT_URL', 'http://localhost:3001'),
      api: envVars.API_URL || 'http://localhost:3002',
      management: getFrameworkAgnosticEnvVar('MANAGEMENT_REDIRECT_URL', 'http://localhost:3005'),
    };
  }
};

/**
 * Gets the onboarding service URL with dynamic host detection
 * @returns The onboarding service URL
 */
export const getOnboardingServiceUrl = (): string => {
  try {
    return getCommonOnboardingServiceUrl();
  } catch (error) {
    logger.warn('Failed to resolve ONBOARDING_SERVICE_URL', { error: String(error) });
    return 'http://localhost:3006';
  }
};

/**
 * Gets the management service URL with dynamic host detection.
 * Used by `<ManagementRedirect />` to bounce client-onboarding users to
 * the management app after their tenant is provisioned.
 * @returns The management service URL
 */
export const getManagementServiceUrl = (): string => {
  try {
    return getCommonManagementServiceUrl();
  } catch (error) {
    logger.warn('Failed to resolve MANAGEMENT_SERVICE_URL', { error: String(error) });
    return 'http://localhost:3005';
  }
};

/**
 * Gets the staff self-service portal URL (my.letsorderup.com). Used by the
 * auth app's invite-accept flow to send a staff-only invitee straight to
 * their home instead of bouncing them through the management app's access
 * gate (identity → management → gate → staff portal, two visible hops).
 * @returns The staff portal URL
 */
export const getStaffServiceUrl = (): string => {
  try {
    return getServiceUrls().staff;
  } catch (error) {
    logger.warn('Failed to resolve STAFF_SERVICE_URL', { error: String(error) });
    return 'http://localhost:3011';
  }
};

/**
 * Gets the site-admin app URL (www.letsorderup.site). Used by the auth app to
 * route a recognized site admin to the admin app when they reach the auth
 * origin without a `returnUrl` — so an admin is never dead-ended or shown the
 * tenant signup form.
 * @returns The admin service URL
 */
export const getAdminServiceUrl = (): string => {
  try {
    return getServiceUrls().admin;
  } catch (error) {
    logger.warn('Failed to resolve ADMIN_SERVICE_URL', { error: String(error) });
    return 'http://localhost:3001';
  }
};

/**
 * Gets information about the current environment for debugging
 * @returns Object with environment detection details
 */
export const getEnvironmentInfo = () => {
  const envVars = getEnvVars();
  const serviceUrls = getServiceUrls();

  return {
    detectedEnvironment: typeof window !== 'undefined' ? 'browser' : 'server',
    nodeEnv: getNodeEnv(),
    appEnv: getAppEnv(),
    availableVars: {
      authServiceUrl: !!envVars.AUTH_SERVICE_URL,
      apiUrl: !!envVars.API_URL,
      appEnv: !!envVars.APP_ENV,
    },
    dynamicUrls: serviceUrls,
    redirectUrls: getAuthRedirectUrls(),
  };
};
